import { Alert, Button, Grid, Input, InputNumber, Spin, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { TbArrowsHorizontal, TbSearch, TbZoomIn, TbZoomOut } from 'react-icons/tb'

import { classNames } from '@/Ship/Helpers/classNames'

import { loadPdfJs } from '../Helpers/diskPdf'
import { zoomLabel } from '../Helpers/diskImageZoom'
import { DiskPdfStore } from '../Stores/DiskPdfStore'

import type { RenderTask } from 'pdfjs-dist'

import styles from './DiskFileViewer.module.css'

const MATCH_CLASS = 'disk-pdf-match'
/** Наскільки заздалегідь (px) малювати сторінку, що наближається до видимої області. */
const PRELOAD_MARGIN = '800px 0px'

/** Підсвітити фрагменти шару тексту, де є запит (без урахування регістру). */
const markMatches = (layer: HTMLElement | null, query: string): void => {
    if (!layer) return

    const needle = query.trim().toLocaleLowerCase('uk')

    layer.querySelectorAll('span').forEach((span) => {
        const hit = needle !== '' && (span.textContent ?? '').toLocaleLowerCase('uk').includes(needle)
        span.classList.toggle(MATCH_CLASS, hit)
    })
}

interface PageProps {
    store: DiskPdfStore
    number: number
    root: HTMLDivElement | null
}

/**
 * Одна сторінка: заглушка потрібного розміру, а полотно й шар тексту
 * малюються, лише коли сторінка підходить до видимої області, і
 * перемальовуються після зміни масштабу.
 */
const DiskPdfPage = observer(({ store, number, root }: PageProps) => {
    const pageRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const textRef = useRef<HTMLDivElement>(null)
    const [visible, setVisible] = useState(false)
    const [size, setSize] = useState<{ width: number; height: number } | null>(null)
    const scale = store.scale
    const doc = store.doc
    const query = store.searchQuery

    useEffect(() => {
        const element = pageRef.current
        if (!element || !root) return undefined

        const observer = new IntersectionObserver(
            (entries) => setVisible(entries.some((entry) => entry.isIntersecting)),
            { root, rootMargin: PRELOAD_MARGIN }
        )

        observer.observe(element)

        return () => observer.disconnect()
    }, [root])

    useEffect(() => {
        if (!visible || doc === null) return undefined

        let cancelled = false
        let renderTask: RenderTask | null = null
        let textLayer: { cancel: () => void } | null = null

        const draw = async (): Promise<void> => {
            const pdfjs = await loadPdfJs()
            const page = await doc.getPage(number)
            const canvas = canvasRef.current
            const layer = textRef.current
            const element = pageRef.current

            if (cancelled || !canvas || !layer || !element) return

            const viewport = page.getViewport({ scale })
            const ratio = window.devicePixelRatio || 1

            setSize({ width: viewport.width, height: viewport.height })
            canvas.width = Math.floor(viewport.width * ratio)
            canvas.height = Math.floor(viewport.height * ratio)
            canvas.style.width = `${Math.floor(viewport.width)}px`
            canvas.style.height = `${Math.floor(viewport.height)}px`

            renderTask = page.render({
                canvas,
                viewport,
                ...(ratio !== 1 ? { transform: [ratio, 0, 0, ratio, 0, 0] } : {}),
            })

            try {
                await renderTask.promise
            } catch {
                // Скасовано (новий масштаб або сторінку прибрали) — нічого не робимо
                return
            }

            if (cancelled) return

            // Шар тексту pdf.js розмічає себе через CSS-змінні масштабу
            element.style.setProperty('--total-scale-factor', String(scale))
            element.style.setProperty('--scale-round-x', '1px')
            element.style.setProperty('--scale-round-y', '1px')
            layer.replaceChildren()

            const text = new pdfjs.TextLayer({
                textContentSource: page.streamTextContent(),
                container: layer,
                viewport,
            })

            textLayer = text

            try {
                await text.render()
            } catch {
                return
            }

            if (!cancelled) markMatches(layer, store.searchQuery)
        }

        void draw()

        return () => {
            cancelled = true
            renderTask?.cancel()
            textLayer?.cancel()
        }
    }, [visible, doc, number, scale, store])

    useEffect(() => {
        markMatches(textRef.current, query)
    }, [query])

    const fallback = store.pageSize
        ? { width: store.pageSize.width * scale, height: store.pageSize.height * scale }
        : { width: 600, height: 800 }
    const shown = size ?? fallback

    return (
        <div
            ref={pageRef}
            className={classNames(styles['pdfPage'])}
            style={{ width: Math.floor(shown.width), height: Math.floor(shown.height) }}
            data-page={number}
            data-testid="disk-pdf-page"
        >
            <canvas ref={canvasRef} className={classNames(styles['pdfCanvas'])} aria-hidden />
            <div ref={textRef} className="textLayer" />
        </div>
    )
})

interface DiskPdfViewProps {
    url: string
    onExpired: () => void
}

/**
 * PDF через pdf.js (`pdfjs-dist`, D-14): усі сторінки однією стрічкою з
 * лінивим малюванням, масштаб («по ширині», «+»/«−»), номер сторінки з
 * переходом, виділення й копіювання тексту (шар тексту), пошук по тексту.
 */
export const DiskPdfView = observer(({ url, onExpired }: DiskPdfViewProps) => {
    const compact = Grid.useBreakpoint().md === false
    const [store] = useState(() => new DiskPdfStore())
    const [root, setRoot] = useState<HTMLDivElement | null>(null)
    const [pageInput, setPageInput] = useState<number | null>(null)

    useEffect(() => {
        void store.load(url, onExpired)
        // Посилання міняється лише на свіже (після 403); `onExpired` за змістом незмінний
    }, [url, store])

    useEffect(() => () => store.destroy(), [store])

    // Ширина для «по ширині»
    useEffect(() => {
        if (!root) return undefined

        const observer = new ResizeObserver(() => store.setContainerWidth(root.clientWidth))

        store.setContainerWidth(root.clientWidth)
        observer.observe(root)

        return () => observer.disconnect()
    }, [root, store])

    // Поточна сторінка — та, що перетинає верхню третину видимої області
    useEffect(() => {
        if (!root) return undefined

        const onScroll = (): void => {
            const line = root.scrollTop + root.clientHeight / 3
            let current = 1

            root.querySelectorAll<HTMLElement>('[data-page]').forEach((page) => {
                if (page.offsetTop <= line) current = Number(page.dataset['page'] ?? 1)
            })

            if (current !== store.currentPage) store.setCurrentPage(current)
        }

        root.addEventListener('scroll', onScroll, { passive: true })

        return () => root.removeEventListener('scroll', onScroll)
    }, [root, store])

    const goTo = (page: number): void => {
        const target = root?.querySelector<HTMLElement>(`[data-page="${page}"]`)

        if (!root || !target) return

        root.scrollTop = target.offsetTop - 8
        store.setCurrentPage(page)
    }

    /** Після переходу — дочекатися шару тексту сторінки й поставити збіг посередині видимої області. */
    const revealMatch = (page: number, attempt = 0): void => {
        const match = root?.querySelector<HTMLElement>(`[data-page="${page}"] .${MATCH_CLASS}`)

        if (match) {
            match.scrollIntoView({ block: 'center', inline: 'nearest' })

            return
        }

        if (attempt < 30) window.setTimeout(() => revealMatch(page, attempt + 1), 100)
    }

    const findNext = async (): Promise<void> => {
        const page = await store.findNext()

        if (page === null) return

        goTo(page)
        revealMatch(page)
    }

    if (store.error !== null) {
        return (
            <div className={classNames(styles['center'])}>
                <Alert type="error" showIcon title={store.error} />
            </div>
        )
    }

    const pages = Array.from({ length: store.pageCount }, (_, index) => index + 1)
    const searchState = !store.searchDone
        ? null
        : store.matches.length === 0
          ? 'Не знайдено'
          : `Збіг на с. ${store.matches[store.matchIndex] ?? ''} · сторінок зі збігами: ${store.matches.length}`

    return (
        <>
            <div className={classNames(styles['tools'])} role="toolbar" aria-label="Перегляд PDF">
                <Tooltip title="Зменшити">
                    <Button type="text" icon={<TbZoomOut />} aria-label="Зменшити" onClick={() => store.zoomOut()} />
                </Tooltip>
                <span className={classNames(styles['zoomValue'])} data-testid="disk-viewer-zoom">
                    {zoomLabel(store.scale)}
                </span>
                <Tooltip title="Збільшити">
                    <Button type="text" icon={<TbZoomIn />} aria-label="Збільшити" onClick={() => store.zoomIn()} />
                </Tooltip>
                <Button
                    type={store.zoom === 'width' ? 'default' : 'text'}
                    icon={<TbArrowsHorizontal />}
                    aria-label="По ширині"
                    onClick={() => store.fitWidth()}
                >
                    {compact ? null : 'По ширині'}
                </Button>
                {store.pageCount > 0 && (
                    <span>
                        <InputNumber
                            className={classNames(styles['pageInput'])}
                            size="small"
                            min={1}
                            max={store.pageCount}
                            controls={false}
                            value={pageInput ?? store.currentPage}
                            aria-label="Номер сторінки"
                            onChange={(value) => setPageInput(typeof value === 'number' ? value : null)}
                            onPressEnter={() => {
                                if (pageInput !== null) goTo(pageInput)
                                setPageInput(null)
                            }}
                            onBlur={() => setPageInput(null)}
                            data-testid="disk-pdf-page-input"
                        />{' '}
                        <span data-testid="disk-pdf-pages">з {store.pageCount}</span>
                    </span>
                )}
                <Input
                    size="small"
                    allowClear
                    style={{ width: compact ? 150 : 180 }}
                    prefix={<TbSearch aria-hidden />}
                    placeholder="Пошук у тексті"
                    aria-label="Пошук у тексті PDF"
                    value={store.searchQuery}
                    onChange={(event) => store.setSearchQuery(event.target.value)}
                    onPressEnter={() => void findNext()}
                    data-testid="disk-pdf-search"
                />
                {store.isSearching && <Spin size="small" />}
                {searchState !== null && (
                    <span className={classNames(styles['zoomValue'])} data-testid="disk-pdf-search-state">
                        {searchState}
                    </span>
                )}
            </div>
            <div ref={setRoot} className={classNames(styles['pdfScroll'])} data-testid="disk-pdf">
                {store.isLoading && (
                    <div className={classNames(styles['center'])}>
                        <Spin />
                    </div>
                )}
                <div className={classNames(styles['pdfPages'])}>
                    {pages.map((number) => (
                        <DiskPdfPage key={number} store={store} number={number} root={root} />
                    ))}
                </div>
            </div>
        </>
    )
})
